import { useMemo } from 'react';

// ─── icons ───────────────────────────────────────────────────────────────────

function CheckCircleIcon() {
  return (
    <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg className="w-8 h-8 text-[#7D93AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-.375c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v.375c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns a criticality score — lower is more critical.
 * Out-of-stock (qty === 0) scores -Infinity so they always sort first.
 * Others score qty / reorderLevel (lower ratio = more critical).
 */
function criticalityScore(item) {
  const qty = Number(item.quantity ?? 0);
  const threshold = Number(item.reorderLevel ?? 0);
  if (qty === 0) return -Infinity;
  if (threshold === 0) return Infinity; // shouldn't be in low-stock list, but guard anyway
  return qty / threshold;
}

function isLowStock(item) {
  const qty = Number(item.quantity ?? 0);
  const threshold = Number(item.reorderLevel ?? 0);
  // Only flag qty===0 when reorderLevel is missing/0
  return qty <= threshold;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function SummaryBar({ totalCount, lowCount }) {
  return (
    <div className="flex items-center justify-between mb-3">
      {/* Left: total tracked */}
      <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">
        <span className="font-semibold text-[#0F1923] dark:text-[#F8FAFE]">{totalCount}</span>{' '}
        item{totalCount !== 1 ? 's' : ''} tracked
      </span>

      {/* Right: low-stock badge or all-good pill */}
      {lowCount > 0 ? (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <span className="text-[var(--wm-text-caption)] font-semibold text-red-700 dark:text-red-400">
            {lowCount} low stock
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <span className="text-[var(--wm-text-caption)] font-semibold text-emerald-700 dark:text-emerald-400">
            All stocked
          </span>
        </div>
      )}
    </div>
  );
}

function InventoryRow({ item }) {
  const qty = Number(item.quantity ?? 0);
  const threshold = Number(item.reorderLevel ?? 0);
  const isOut = qty === 0;

  const borderClass = isOut
    ? 'border-l-2 border-red-400'
    : 'border-l-2 border-amber-400';

  const qtyColor = isOut ? '#EF4444' : '#F59E0B';

  const unitLabel = item.unit ? ` ${item.unit}` : '';

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-md bg-gray-50/60 dark:bg-[#13202E]/60 hover:bg-gray-100/80 dark:hover:bg-[#1B2A3E]/80 transition-colors ${borderClass}`}
    >
      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate">
            {item.name}
          </span>
          {isOut && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[var(--wm-text-caption)] font-bold bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 flex-shrink-0">
              OUT
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {item.category && (
            <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
              {item.category}
            </span>
          )}
          {item.category && (
            <span className="text-[var(--wm-text-caption)] text-[#94A3B8] dark:text-[#3D5470]">·</span>
          )}
          <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
            Reorder at: {threshold}{unitLabel}
          </span>
        </div>
      </div>

      {/* Qty — right-aligned, large-ish */}
      <div className="text-right flex-shrink-0">
        <span
          className="text-[var(--wm-text-ui)] font-bold tabular-nums"
          style={{ color: qtyColor }}
        >
          {qty}
        </span>
        {unitLabel && (
          <span className="text-[var(--wm-text-caption)] text-[#94A3B8] dark:text-[#3D5470] ml-0.5">
            {item.unit}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── empty states ─────────────────────────────────────────────────────────────

function EmptyNoItems() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#13202E] flex items-center justify-center mb-3">
        <BoxIcon />
      </div>
      <p className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">
        No inventory items yet
      </p>
      <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-1 max-w-[220px]">
        Add your first item to track stock.
      </p>
    </div>
  );
}

function EmptyAllStocked({ totalCount }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
        <CheckCircleIcon />
      </div>
      <p className="text-[var(--wm-text-body)] font-semibold text-emerald-700 dark:text-emerald-400">
        All stocked up!
      </p>
      <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-1">
        Every item is above its reorder level.
      </p>
      <p className="text-[var(--wm-text-caption)] text-[#94A3B8] dark:text-[#3D5470] mt-0.5">
        {totalCount} item{totalCount !== 1 ? 's' : ''} tracked
      </p>
    </div>
  );
}

// ─── main widget ─────────────────────────────────────────────────────────────

const MAX_ROWS = 8;

export default function InventoryAlertsWidget() {
  const { allItems, lowStockItems } = useMemo(() => {
    try {
      const raw = localStorage.getItem('wm-inventory-v1');
      if (!raw) return { allItems: [], lowStockItems: [] };
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return { allItems: [], lowStockItems: [] };

      const low = parsed
        .filter(isLowStock)
        .sort((a, b) => criticalityScore(a) - criticalityScore(b));

      return { allItems: parsed, lowStockItems: low };
    } catch {
      return { allItems: [], lowStockItems: [] };
    }
  }, []);

  // ── empty: no inventory at all ──────────────────────────────────────────────
  if (allItems.length === 0) {
    return <EmptyNoItems />;
  }

  // ── empty: items exist but nothing low ─────────────────────────────────────
  if (lowStockItems.length === 0) {
    return <EmptyAllStocked totalCount={allItems.length} />;
  }

  // ── low-stock list ──────────────────────────────────────────────────────────
  const visibleRows = lowStockItems.slice(0, MAX_ROWS);
  const overflow = lowStockItems.length - visibleRows.length;

  return (
    <div>
      <SummaryBar totalCount={allItems.length} lowCount={lowStockItems.length} />

      <div className="space-y-1.5">
        {visibleRows.map((item) => (
          <InventoryRow key={item.id} item={item} />
        ))}
      </div>

      {overflow > 0 && (
        <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-2.5 text-center">
          + {overflow} more low-stock item{overflow !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
